// components/HistoryCustomerSelector.tsx

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useCustomer } from '../../../../hooks/useCustomer';
import { useReceivableTable } from '../../table/hooks/useReceivableTable';

import SectionInfoHeader from '../../../../components/common/SectionInfoHeader';
import SvgIcon from '../../../../components/SvgIcon/SvgIcon';
import QuickAddCustomer from '../../../../components/QuickAdd/Customer/QuickAddCustomer';
import SimpleSearchSelect from '../../../../components/Forms/Input/SimpleSearchSelect';

import SearchSelectionCard from '../../payments/components/SearchSelectionCard/SearchSelectionCard';

const HistoryCustomerSelector = () => {
  const navigate = useNavigate();

  const { handlerGetCustomersForDropdown, customersDropdown, saveCustomer } =
    useCustomer();

  const {
    fetchPaymentHistoryReceivablesByCustomer,
    pendingCustomerReceivables,
  } = useReceivableTable();

  const [selectedCustomerId, setSelectedCustomerId] = useState<number | null>(
    null
  );

  const [selectedReceivableId, setSelectedReceivableId] = useState<
    number | null
  >(null);

  /**
   * 🔥 cargar clientes
   */
  useEffect(() => {
    handlerGetCustomersForDropdown();
  }, []);

  /**
   * 🔥 cargar cuentas del cliente
   *
   * Para historial NO solo queremos pendientes,
   * luego puedes cambiar este endpoint si haces
   * uno exclusivo de historial.
   */
  useEffect(() => {
    if (!selectedCustomerId) return;

    fetchPaymentHistoryReceivablesByCustomer(selectedCustomerId);
  }, [selectedCustomerId]);

  /**
   * 🔥 navegación al historial
   */
  const handleReceivableSelect = (item: {
    id: number;
    number: string;
    issueDate: string;
    dueDate: string;
    totalAmount: number;
    paidAmount: number;
    balance: number;
    status: string;
    daysOverdue: number;
  }) => {
    setSelectedReceivableId(item.id);

    navigate(`/finance/cxc/payment-history/${item.id}`);
  };

  /**
   * 🔥 búsqueda backend
   */
  const handleSearchReceivables = async (search: string) => {
    if (!selectedCustomerId) return;

    await fetchPaymentHistoryReceivablesByCustomer(selectedCustomerId, search);
  };

  return (
    <div className="px-6 py-6">
      <SectionInfoHeader
        icon={<SvgIcon name="user" />}
        title="Historial de Cobros a Clientes"
        description="Busca un cliente para consultar el historial de pagos."
        color="bg-success"
      />

      {/* Quick Add */}
      <div className="mb-4">
        <QuickAddCustomer
          onReload={handlerGetCustomersForDropdown}
          onCustomerCreated={saveCustomer}
        />
      </div>

      {/* Cliente */}
      <div className="mb-5 max-w-xl">
        <SimpleSearchSelect
          label="Cliente"
          name="customer"
          iconKey="user"
          selectedValue={selectedCustomerId}
          onSelect={(option) => {
            setSelectedCustomerId(option.id);
            setSelectedReceivableId(null);
          }}
          options={customersDropdown}
          required
        />
      </div>

      {/* Cuentas */}
      <SearchSelectionCard
        title="Seleccionar cuenta"
        description="Busca una cuenta para consultar su historial de pagos"
        label="Buscar cuenta"
        name="receivable-history"
        iconKey="invoice"
        options={pendingCustomerReceivables}
        selectedValue={selectedReceivableId}
        onSelect={handleReceivableSelect}
        onSearch={handleSearchReceivables}
        listTitle="Cuentas disponibles"
        emptyMessage="No hay cuentas disponibles para este cliente"
        disabled={!selectedCustomerId}
        required
      />
    </div>
  );
};

export default HistoryCustomerSelector;
